import { Outlet } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useJobs } from '@/features/job/queries'
import { useRealtimeConnection } from '@/features/realtime/use-realtime'
import { useUndoRedoShortcut } from '@/features/undo-redo/use-undo-redo'
import { useSessionStore } from '@/features/auth/session-store'
import { useLithosConsent } from '@/shared/lib/lithos-consent'
import { SidebarInset, SidebarProvider } from '@/shared/ui/sidebar'
import { AppFooter } from './app-footer'
import { AppSidebar } from './app-sidebar'
import { ConnectionBanner } from './connection-banner'

/**
 * The frame every signed-in route renders inside.
 *
 * It draws no chrome bar of its own: the sidebar toggle and the notification bell
 * live in the sidebar and in each page's own bar, so no route pays a whole row of
 * height for two controls.
 *
 * This is where the websocket is held open. One mount, at the top of the signed-in
 * tree: the connection is ref-counted and would survive being acquired lower down,
 * but a consumer that unmounts on navigation would churn the page subscriptions
 * with it, and there is nothing below this that outlives fewer navigations.
 */
export function AppShell() {
  useRealtimeConnection()
  // Subscribed here so the cache entry exists for the whole session. The
  // `job_started` handler patches this key and does nothing when nobody has
  // fetched it, which would leave a job started in another tab invisible until
  // something else happened to ask.
  useJobs()
  useUndoRedoShortcut()

  // The suite's consent record names its person, so somebody else signing in on
  // this browser is asked again rather than inheriting a choice. The username is
  // the account's email.
  const consent = useLithosConsent()
  const email = useSessionStore((state) => state.user?.username)
  useEffect(() => {
    if (consent !== null && email !== undefined) void consent.identify(email)
  }, [consent, email])

  return (
    <SidebarProvider defaultOpen={false}>
      <AppSidebar />
      {/*
        The signature wash. It is on the inset rather than the wrapper because the
        sidebar paints its own opaque surface, and a page's own surfaces --
        `bg-card`, the grid -- sit on top of it.
      */}
      {/*
        `min-w-0` so the column may be narrower than what is in it. Without it a
        page holding something wide -- a table's own bar, a grid -- floors the
        inset at that width, and the whole application scrolls sideways under a
        grid that is already scrolling sideways by itself.
      */}
      <SidebarInset className="app-background min-w-0">
        <ConnectionBanner />
        {/*
          The room a page gets, and not a scroll container. Scrolling belongs to
          the page: a table scrolls its rows and nothing else, and a page of
          prose scrolls the prose. A container here would be a second one around
          both, which is the pair of bars nobody asked for.
        */}
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <Outlet />
        </div>
        <AppFooter consent={consent} />
      </SidebarInset>
    </SidebarProvider>
  )
}
